/// <amd-module name="Widgets/Burndown/BurndownWidgetComponents/TimeIntervalUtils" />
import { Iteration } from "Analytics/Boards/BoardsQueries/IterationsQuery";
import {
    addDays,
    addMonths,
    DateSampleInterval,
    getEndOfDateIntervalPeriod,
    getEndOfMonth,
    getStartOfMonth,
    parseIsoDateStringInLocalTime,
    toLocalDateIsoString
} from "WidgetCommon/DateUtils/DateUtils";
import * as Resources from "Widgets/Burndown/Resources";
import { DateSamplingConfiguration } from "./BurndownSettings";

export interface DateSampleSeries {
    sampleDates: string[];
    lastIntervalDate: string;

    /** Describes if the dates are a continuous series of daily samples.
     *  Query generation uses set comprehension for daily samples, and uses set enumeration otherwise. */
    isDailyInterval?: boolean;
}

export class TimeIntervalUtils {
    private static isIterations(info: Iteration[] | DateSamplingConfiguration): info is Iteration[] {
        const samplingConfig = info as DateSamplingConfiguration;
        return typeof samplingConfig.lastDayOfWeek === "undefined" && typeof samplingConfig.sampleInterval === "undefined";
    }

    /** Returns iteration end dates. Requires all supplied iterations to have end dates. */
    private static getIterationEndDates(iterations: Iteration[]): string[] {
        if (iterations == null) {
            throw new Error(Resources.ConfigError_NoIterations);
        }

        return iterations.map(iteration => {
            if (iteration.EndDateTimeOffset == null) {
                throw new Error(Resources.ConfigError_NoEndDateOnIteration); // This should be dev-facing guard, but a user seeing this might be able to work around if they were to encounter it.
            }

            return iteration.EndDateTimeOffset.substr(0, "yyyy-MM-dd".length);
        });
    }

    private static stripTimeFromDate(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }
    public static getSampleDates(startDateString: string, iterations: Iteration[]): DateSampleSeries;
    public static getSampleDates(startDateString: string, dateSamplingConfiguration: DateSamplingConfiguration): DateSampleSeries;
    public static getSampleDates(startDateString: string, endDateSamplingConfiguration: Iteration[] | DateSamplingConfiguration): DateSampleSeries {
        // Dates always has the start date in it first.
        const dates: string[] = [startDateString];
        let lastIntervalDate: string | undefined = undefined;
        let isDailyInterval = false;

        if (TimeIntervalUtils.isIterations(endDateSamplingConfiguration)) {
            dates.push(...TimeIntervalUtils.getIterationEndDates(endDateSamplingConfiguration));
        } else {
            const startDate = parseIsoDateStringInLocalTime(startDateString);

            const { endDate: endDateString, lastDayOfWeek, sampleInterval } = endDateSamplingConfiguration;
            const endDate = parseIsoDateStringInLocalTime(endDateString);
            let endOfIntervalDate = getEndOfDateIntervalPeriod(startDate, sampleInterval, lastDayOfWeek);
            isDailyInterval = sampleInterval === DateSampleInterval.Days;

            // Append interval dates
            while (
                TimeIntervalUtils.stripTimeFromDate(endOfIntervalDate).getTime() < TimeIntervalUtils.stripTimeFromDate(endDate).getTime() ||
                TimeIntervalUtils.stripTimeFromDate(endOfIntervalDate).getTime() == TimeIntervalUtils.stripTimeFromDate(endDate).getTime()
            ) {
                if (endOfIntervalDate.getTime() !== startDate.getTime()) {
                    dates.push(toLocalDateIsoString(endOfIntervalDate));
                }

                if (sampleInterval === DateSampleInterval.Months) {
                    /*
                     * We want to append the last date of the next month. To do this we shift to the start of the current month before adding a month.
                     * This way we dont accidently skip a month if the current date is the 31st.
                     */
                    endOfIntervalDate = getEndOfMonth(addMonths(getStartOfMonth(endOfIntervalDate), 1));
                } else {
                    endOfIntervalDate = addDays(endOfIntervalDate, 1 * sampleInterval == DateSampleInterval.Weeks ? 7 : 1);
                }
            }

            // Remember the last date that fits the interval (or start date if we didn't generate any more)
            lastIntervalDate = dates[dates.length - 1];

            // If the end date didn't match up to an interval, add it
            if (endDateString !== dates[dates.length - 1]) {
                dates.push(endDateString);
            }
        }

        return {
            sampleDates: dates,
            lastIntervalDate: lastIntervalDate || "",
            isDailyInterval: isDailyInterval
        };
    }
}
